import { useState } from "react";
import { FactCheckCard } from "@/components/public/FactCheckCard";
import { useI18n } from "@/lib/i18n";
import { useVerifications } from "@/hooks/useVerifications";
import type { Verdict } from "@/lib/mock-data";
import { Loader2, RefreshCw } from "lucide-react";
import { adaptVerifications } from "@/lib/adapters";
import { useSeo, useJsonLd } from "@/hooks/useSeo";
import { SITE_URL } from "@/lib/seo";

const VERDICT_FILTERS: (Verdict | "all")[] = ["all", "false", "misleading", "unproven", "true", "context"];

function FactCheckPage() {
  const { t, locale } = useI18n();
  const [filter, setFilter] = useState<Verdict | "all">("all");

  useSeo({
    title: `${t("section.factcheck")} — Ramba News`,
    description: t("factcheck.intro"),
    path: "/factcheck",
  });

  useJsonLd("ld-breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: locale === "fr" ? "Accueil" : "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: t("section.factcheck"), item: `${SITE_URL}/factcheck` },
    ],
  });

  const { data, isLoading, isError, refetch } = useVerifications({ estPublic: true });

  const rawDtos = Array.isArray(data) ? data : [];
  const allItems = adaptVerifications(rawDtos);
  const items = filter === "all" ? allItems : allItems.filter((f) => f.verdict === filter);

  const filterLabel = (id: Verdict | "all") => {
    if (id === "all") return t("filter.all");
    if (id === "context") return t("factcheck.filter.context");
    return t(`verdict.${id}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 md:py-14">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-verified">Fact-check</div>
      <h1 className="mt-2 font-serif text-3xl md:text-5xl font-semibold tracking-tight">
        {t("section.factcheck")}
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        {t("factcheck.intro")}
      </p>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-border pb-3">
        {VERDICT_FILTERS.map((id) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === id
                ? "bg-foreground text-background"
                : "bg-surface text-muted-foreground hover:text-foreground"
            }`}
          >
            {filterLabel(id)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="mt-16 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">{t("common.loading")}</span>
        </div>
      ) : isError ? (
        <div className="mt-16 flex flex-col items-center justify-center gap-3">
          <p className="text-sm text-muted-foreground">{t("factcheck.loadError")}</p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold hover:bg-surface transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> {t("cta.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="mt-16 text-center text-sm text-muted-foreground">
          {t("factcheck.emptyFilter")}
        </div>
      ) : (
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((f) => <FactCheckCard key={f.id} fc={f} />)}
        </div>
      )}
    </div>
  );
}

export default FactCheckPage;
